import { useEffect, useState } from "react";
import { gql } from "@apollo/client";
import { useApolloClient } from "@apollo/client/react";
import type { ApolloClient } from "@apollo/client";

/**
 * Cover thumbnail URL of one document, for recognising a source on a card.
 *
 * A reference list mounts a dozen cards in the same tick, and one request per card would
 * undo the point of a thumbnail. Lookups are therefore collected for one microtask and sent
 * as a single `documentCovers` query. Decorative by design: while loading, when nothing is rendered
 * yet, and on any error the hook returns null and the card keeps its file icon.
 */

const COVERS = gql`
  query DocumentCovers($documentIds: [UUID!]!) {
    covers: documentCovers(documentIds: $documentIds) {
      documentId
      coverUrl
    }
  }
`;

interface CoversData {
    covers: { documentId: string; coverUrl: string | null }[];
}

// Module-level on purpose: the batch and the answers must outlive any one card, or every
// remount would ask again. Single-threaded JS, and the signed URLs are immutable for a
// document's bytes, so a cached answer never goes stale within a page session. Keyed by
// client so a tenant switch (new client) cannot read another tenant's answers.
const answers = new WeakMap<object, Map<string, string | null>>();
const pending = new WeakMap<object, Map<string, ((url: string | null) => void)[]>>();

function cacheFor(client: object): Map<string, string | null> {
    let cache = answers.get(client);
    if (!cache) {
        cache = new Map();
        answers.set(client, cache);
    }
    return cache;
}

function request(client: ApolloClient, documentId: string): Promise<string | null> {
    const cache = cacheFor(client);
    if (cache.has(documentId)) return Promise.resolve(cache.get(documentId) ?? null);

    return new Promise((resolve) => {
        let queue = pending.get(client);
        const first = !queue;
        if (!queue) {
            queue = new Map();
            pending.set(client, queue);
        }
        const waiters = queue.get(documentId) ?? [];
        waiters.push(resolve);
        queue.set(documentId, waiters);
        if (!first) return;

        queueMicrotask(() => {
            const batch = pending.get(client);
            pending.delete(client);
            if (!batch) return;
            const ids = [...batch.keys()];
            const settle = (urls: Map<string, string | null>) => {
                for (const [id, resolvers] of batch) {
                    const url = urls.get(id) ?? null;
                    cache.set(id, url);
                    resolvers.forEach((r) => r(url));
                }
            };
            client
                .query<CoversData>({ query: COVERS, variables: { documentIds: ids } })
                .then(({ data }) =>
                    settle(new Map((data?.covers ?? []).map((c) => [c.documentId, c.coverUrl]))),
                )
                .catch(() => {
                    // Not cached: a transient failure should be retried by a later mount.
                    for (const resolvers of batch.values()) resolvers.forEach((r) => r(null));
                });
        });
    });
}

export function useDocumentCover(documentId: string | null | undefined): string | null {
    const client = useApolloClient();
    const [url, setUrl] = useState<string | null>(() =>
        documentId ? (cacheFor(client).get(documentId) ?? null) : null,
    );

    useEffect(() => {
        if (!documentId) {
            setUrl(null);
            return;
        }
        let cancelled = false;
        void request(client, documentId).then((u) => {
            if (!cancelled) setUrl(u);
        });
        return () => {
            cancelled = true;
        };
    }, [client, documentId]);

    return url;
}
